/**
 * @file     MapPaneFallback.tsx
 * @purpose  The map pane before the map view chunk loads: the low static map
 *           of the default view in the live pane's exact box (full-bleed under
 *           the sheet on phones, the left column at md and tablet portrait).
 *           Painted by the server shell, so the first frame's largest element
 *           is the map and no later panel content can become a larger LCP.
 */
import Box from '@mui/material/Box';
import { MapStaticPlaceholder } from '@/components/interactive-map/MapStaticPlaceholder';
import { DESKTOP_MAP_WIDTH_PCT } from '../panelLayout';

const sideBySide = { top: 0, bottom: 0, left: 0, right: 'auto', width: `${DESKTOP_MAP_WIDTH_PCT}%` } as const;

const paneSx = {
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
  '@media (min-width: 900px)': sideBySide,
  '@media (min-width: 768px) and (orientation: portrait)': sideBySide,
} as const;

/** Server shell: `auto` (the browser picks the theme by prefers-color-scheme).
 *  The client passes the resolved mode once the theme is known. */
export function MapPaneFallback({ mode = 'auto' }: { mode?: 'light' | 'dark' | 'auto' }) {
  return (
    <Box aria-hidden sx={paneSx}>
      <MapStaticPlaceholder mode={mode} quality="low" priority />
    </Box>
  );
}
